import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/material/styles';

// Clignotement vert qui amene l'oeil sur la cible du moment (chip racine, Play,
// Jam). Un seul endroit pour les trois, sinon les durees divergent.
const coachBlink = keyframes`
  0%, 100% { box-shadow: inset 0 0 0 1px rgba(var(--hl-root-rgb),0.4), 0 0 0 0 rgba(var(--hl-root-rgb),0); }
  50% { box-shadow: inset 0 0 0 2px var(--hl-root), 0 0 18px 4px rgba(var(--hl-root-rgb),0.55); }
`;

export const coachBlinkSx = {
  animation: `${coachBlink} 1.1s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
};

// Le tip peut deborder sur la bande des numeros de frettes, jamais couvrir une
// note : sur un chip de bord, on l'aligne du bon cote plutot que de le centrer,
// sinon la moitie du texte sort de l'ecran en paysage compact.
const ANCHORED_ALIGN = {
  start: { left: 0, arrow: { left: '24px' } },
  center: { left: '50%', transform: 'translateX(-50%)', arrow: { left: '50%', marginLeft: '-5px' } },
  end: { right: 0, arrow: { right: '24px' } }
};

// 'down' vise le parent (la cible a taper), 'up' vise le manche au-dessus
// (la consigne parle de la guitare, pas du chip), 'none' pour une simple bulle.
const ARROW_SIDE = {
  down: { bottom: '-4px' },
  up: { top: '-4px' }
};

const anchoredSx = (align, arrow) => {
  const { arrow: arrowAlign, ...position } = ANCHORED_ALIGN[align] || ANCHORED_ALIGN.center;
  const side = ARROW_SIDE[arrow];
  return {
    position: 'absolute',
    bottom: 'calc(100% + 12px)',
    ...position,
    whiteSpace: 'nowrap',
    backgroundColor: '#fff',
    color: '#0a1929',
    fontSize: '1.15rem',
    fontWeight: 700,
    padding: '9px 16px',
    borderRadius: '9px',
    boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
    zIndex: 20,
    pointerEvents: 'none',
    // L'ecran est souvent a un metre, pose sur un pupitre.
    '@media (pointer: coarse)': { fontSize: '1.725rem' },
    ...(side && {
      '&::after': {
        content: '""',
        position: 'absolute',
        width: '10px',
        height: '10px',
        backgroundColor: '#fff',
        transform: 'rotate(45deg)',
        ...side,
        ...arrowAlign
      }
    })
  };
};

/**
 * Coup de pouce affiche a cote de la cible du moment.
 *
 * Bulle blanche avec sa fleche, posee au-dessus du parent (qui doit etre en
 * position relative).
 *
 * La disparition vit ici : timer, ou premiere interaction, jamais de bouton a
 * fermer. Sans `onDismiss` le tip reste (cas du tooltip d'ouverture, qui attend
 * le premier accord).
 */
const Tip = ({ text, align = 'center', arrow = 'down', duration = null, onDismiss }) => {
  const [visible, setVisible] = useState(true);
  const rootRef = useRef(null);

  useEffect(() => { setVisible(true); }, [text]);

  useEffect(() => {
    if (!onDismiss) return undefined;

    let done = false;
    // L'evenement qui a fait apparaitre le tip ne doit pas l'effacer : on n'arme
    // l'ecoute qu'au tick suivant.
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 0);

    const dismiss = (reason) => {
      if (done) return;
      done = true;
      setVisible(false);
      onDismiss(reason);
    };

    const handlePointerDown = (event) => {
      if (!armed) return;
      // Un tap sur la cible elle-meme doit rester un tap : sans ca le tooltip
      // Play se fermerait avant que le clic n'arrive au bouton.
      if (rootRef.current?.parentElement?.contains(event.target)) return;
      dismiss('interaction');
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    const hideTimer = duration == null ? null : setTimeout(() => dismiss('timer'), duration);

    return () => {
      clearTimeout(armTimer);
      if (hideTimer) clearTimeout(hideTimer);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [text, duration, onDismiss]);

  if (!visible) return null;

  return (
    <Box ref={rootRef} sx={anchoredSx(align, arrow)}>
      {text}
    </Box>
  );
};

export default Tip;
